import { Box, Typography } from "@mui/material";
import {
  CommonButton,
  CustomRequiredInputLabel,
} from "../../common/StyledComponents/StyledFields";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import CustomSelectField from "../../common/CustomSelectField/CustomSelectField";
import ErrorPopup from "../../common/ErrorPopup";
import {
  apiRequest,
  catchErrorMessageHandler,
} from "../../../infrastructure/backendService";
import toast from "react-hot-toast";
import apiPath from "../../../config/apiPaths.json";
import constants from "../../../config/constants.json";
import styles from "./ExpenseRequest.module.scss";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";
import Pagination from "../../common/pagination/pagination";
import { useAppDispatch, useAppSelector } from "../../../app/hook";
import {
  addExpenseCategories,
  addExpenseCategoryAccountNumbers,
  fetchExpenseDetailsBudget,
  removeSelectedAccountNumber,
  setSelectedAccountNumber,
  setSelectedExpenseCategory,
} from "../../../features/epxenseRequestSlice";
import CreateExpenseRequest from "./CreateExpenseRequest/CreateExpenseRequest";
import ViewExpenseRequest from "../PrintVoucher/ViewExpenseRequest/ViewExpenseRequest";
import printIcon from "../../../assets/PrintIcon.svg";
import editIcon from "../../../assets/EditIcon.svg";
import CreateSubAccount from "../SubAccounts/CreateSubAccount";

export const groupingStyles = {
  fieldContainer: {
    maxWidth: { xs: "100%", sm: "320px" },
    width: { xs: "100%", sm: "320px" },
    pr: { xs: "0", sm: "15px" },
    mb: "10px",
  },
  groupedFieldsContainer: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
  },
};

const ExpenseRequest = () => {
  const dispatch = useAppDispatch();
  const { expenseCategories, expenseCategoryAccountNumbers } = useAppSelector(
    (state) => state.expenseRequest
  );

  const currentYear = new Date()?.getFullYear();
  const createExpenseRequestYears = [
    currentYear?.toString(),
    (currentYear - 1)?.toString(),
  ];

  const [expenseCategoryAccountYears, setExpenseCategoryAccountYears] =
    useState<{ year: string }[]>([]);

  const [expenseDetails, setExpenseDetails] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [createExpenseRequest, setCreateExpenseRequest] =
    useState<boolean>(false);
  const [openExpenseRequestDialog, setOpenExpenseRequestDialog] =
    useState(false);
  const [viewRequestDetails, setViewRequestDetails] = useState(false);
  const [updateExpenseRequest, setUpdateExpenseRequest] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [fetchedSubAccounts, setFetchedSubAccounts] = useState(false);
  const [openAddSubAccountDialog, setOpenAddSubAccountDialog] = useState(false);

  const initialValues = {
    committee: "",
    accountNumber: "",
    year: new Date()?.getFullYear()?.toString(),
    displayYearField: false,
  };

  const formik = useFormik({
    initialValues,
    onSubmit(values) {},
    enableReinitialize: true,
  });

  const getExpenseCategories = () => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.expenseCategories,
      includeToken: true,
    })
      .then((res) => {
        dispatch(addExpenseCategories(res?.data));
      })
      .catch((err) => {
        console.log(err);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const getExpenseCategoryAccounts = (categoryAccountId: string) => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.expenseCategoryAccounts?.replace(
        "{categoryAccountId}",
        categoryAccountId
      ),
      includeToken: true,
    })
      .then((res) => {
        setFetchedSubAccounts(true);
        dispatch(addExpenseCategoryAccountNumbers(res?.data));
      })
      .catch((err) => {
        setFetchedSubAccounts(true);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const getExpenseCategoryAccountYears = (accountId: string) => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.expenseCategoryAccountYears?.replace(
        "{accountId}",
        accountId
      ),
      includeToken: true,
    })
      .then((res) => {
        let data = res?.data?.map((year: string) => ({ year: year }));
        data = data?.filter((each: any) => Number(each?.year) <= currentYear);
        setExpenseCategoryAccountYears(data);
      })
      .catch((err) => {
        console.log(err);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const getExpenseDetails = () => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.expenseDetails
        ?.replace("{accountId}", formik.values.accountNumber)
        ?.replace("{year}", formik.values.year),
      includeToken: true,
      params: {
        page: pageNumber - 1,
        size: 20,
        direction: "desc",
        sortBy: "dateEntered",
      },
    })
      .then((res) => {
        setExpenseDetails(res?.data?.content);
        setTotalRecords(res?.data?.totalElements);
        const element = document.getElementById("expense_details");
        if (element) {
          element?.scrollIntoView({ behavior: "smooth", block: "start" });
          // Apply additional offset for a custom position
          setTimeout(() => {
            window.scrollBy(0, -250); // Move 20px up from the scrolled position
          }, 500); // Delay to ensure smooth scroll completes
        }
      })
      .catch((err) => {
        console.log(err);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  useEffect(() => {
    getExpenseCategories();
  }, []);

  useEffect(() => {
    const { accountNumber, committee, year } = formik.values;
    if (committee && accountNumber && year && !createExpenseRequest) {
      getExpenseDetails();
    }
  }, [formik.values, pageNumber, createExpenseRequest]);

  const handleFieldChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
    setPageNumber(1);
    if (field === "committee") {
      const expenseCategoryData = expenseCategories?.find(
        (data: any) => data?.accountId === value
      );
      getExpenseCategoryAccounts(value);
      dispatch(setSelectedExpenseCategory(expenseCategoryData));
      dispatch(removeSelectedAccountNumber());
      formik.setFieldValue("accountNumber", "");
    } else if (field === "accountNumber") {
      const accountNumberData = expenseCategoryAccountNumbers?.find(
        (data: any) => data?.accountId === value
      );
      getExpenseCategoryAccountYears(value);
      dispatch(setSelectedAccountNumber(accountNumberData));
      formik.setFieldValue("displayYearField", true);
    }
  };

  const handleCreateExpenseRequest = () => {
    dispatch(
      fetchExpenseDetailsBudget({
        accountId: formik.values.committee,
        subAccountId: formik.values.accountNumber,
        year: formik.values.year,
      })
    ).then(() => {
      setCreateExpenseRequest(true);
    });
  };

  const handlePrint = (referenceNum: any) => {
    setOpenExpenseRequestDialog(true);
    setReferenceNumber(referenceNum);
  };
  const handleView = (referenceNum: any) => {
    setOpenExpenseRequestDialog(true);
    setViewRequestDetails(true);
    setReferenceNumber(referenceNum);
  };

  const handleEdit = (referenceNum: any) => {
    setUpdateExpenseRequest(true);
    setReferenceNumber(referenceNum);
    handleCreateExpenseRequest();
  };

  const handleOpenAddSubAccount = () => {
    setOpenAddSubAccountDialog(true);
  };

  const handleAddSubAccount = (subAccount: any) => {
    handleClose();
    dispatch(
      addExpenseCategoryAccountNumbers([
        ...expenseCategoryAccountNumbers,
        subAccount,
      ])
    );
    formik.setFieldValue("accountNumber", subAccount?.accountId);
    getExpenseCategoryAccountYears(subAccount?.accountId);
    dispatch(setSelectedAccountNumber(subAccount));
    formik.setFieldValue("displayYearField", true);
  };

  const handleClose = () => {
    setOpenAddSubAccountDialog(false);
  };

  const renderFieldsComponent = () => (
    <Box className="common_box_shadow" sx={{ mt: "20px" }}>
      <Box sx={{ ...groupingStyles.groupedFieldsContainer, flexWrap: "wrap" }}>
        <Box sx={groupingStyles.fieldContainer}>
          {CustomRequiredInputLabel("Committee", true)}
          <CustomSelectField
            handleDropdownChange={handleFieldChange}
            errorMessage={formik.errors.committee}
            dropDownList={expenseCategories}
            value={formik.values.committee}
            fieldName="committee"
            placeholder="Committee"
            optionLabel="description"
            optionValue="accountId"
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-end", flexWrap: "wrap" }}>
          {fetchedSubAccounts && expenseCategoryAccountNumbers?.length > 0 && (
            <Box sx={groupingStyles.fieldContainer}>
              {CustomRequiredInputLabel("Select Sub-Account", true)}
              <CustomSelectField
                handleDropdownChange={handleFieldChange}
                errorMessage={
                  formik.touched.accountNumber
                    ? formik.errors.accountNumber
                    : ""
                }
                dropDownList={expenseCategoryAccountNumbers}
                value={formik.values.accountNumber}
                fieldName="accountNumber"
                placeholder="Account number"
                optionLabel="description"
                optionValue="accountId"
                isLabelValue={true}
              />
            </Box>
          )}
          {fetchedSubAccounts && (
            <CommonButton onClick={handleOpenAddSubAccount} sx={{ mb: "15px" }}>
              <ButtonLoader isBtnLoading={false} btnText={`Add Sub Account`} />
            </CommonButton>
          )}
        </Box>
      </Box>
      {expenseCategoryAccountNumbers?.length > 0 && (
        <Box sx={groupingStyles.groupedFieldsContainer}>
          {formik.values.displayYearField && (
            <Box sx={groupingStyles.fieldContainer}>
              {CustomRequiredInputLabel("Year", true)}
              <CustomSelectField
                handleDropdownChange={handleFieldChange}
                errorMessage={formik.errors.year}
                dropDownList={expenseCategoryAccountYears}
                value={formik.values.year}
                fieldName="year"
                placeholder="Select year"
                optionLabel="year"
                optionValue="year"
              />
            </Box>
          )}
        </Box>
      )}
      {fetchedSubAccounts && !(expenseCategoryAccountNumbers?.length > 0) && (
        <Typography
          className="sub_heading"
          sx={{ fontSize: "16px !important", color: "#F05802 !important" }}
        >
          No Sub-accounts found.
        </Typography>
      )}
    </Box>
  );

  const renderEVoucherRequestsTable = () => {
    return (
      <Box mt="25px" className={styles.expense_request_details_container}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography className={styles.expense_request_txt}>
            In Process E-Vouchers
          </Typography>
          <CommonButton
            onClick={handleCreateExpenseRequest}
            sx={{ marginLeft: "16px" }}
            disabled={
              !createExpenseRequestYears?.includes(
                formik.values.year?.toString()
              )
            }
          >
            <ButtonLoader isBtnLoading={false} btnText={"Create New"} />
          </CommonButton>
        </Box>
        {expenseDetails?.length > 0 ? (
          <>
            <Box
              sx={{ mt: "15px", width: "100%", overflowX: "auto" }}
              id="expense_details"
            >
              <table className={styles.table_container}>
                <thead>
                  <th>Sr No</th>
                  <th>Submitted on</th>
                  <th>Status</th>
                  <th>
                    Short Title <br />
                    (Reference Number)
                  </th>
                  <th>Amount</th>
                  <th className="display_none_ipad">Expense Details</th>
                  <th>Action</th>
                </thead>
                <tbody>
                  {expenseDetails?.map?.((row: any, index: number) => (
                    <tr key={index}>
                      <td>{index + 1 + (pageNumber - 1) * 20}</td>
                      <td>{row?.dateEntered}</td>
                      <td>{row?.status}</td>
                      <td
                        className={styles.table_desc_txt}
                        onClick={() => handleView(row?.referenceNumber)}
                        style={{
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        {row?.shortTitle} ({row?.referenceNumber})
                      </td>
                      <td className={styles.amount_text}>
                        ${row?.amount?.toFixed(2)}
                      </td>
                      <td
                        className={`${styles.table_desc_txt} display_none_ipad`}
                      >
                        {row?.expenseDetails}
                      </td>
                      <td>
                        {/* <Typography
                          onClick={() => handlePrint(row?.referenceNumber)}
                        >
                          Print
                        </Typography>
                        <Typography
                          onClick={() => handleEdit(row?.referenceNumber)}
                        >
                          Edit
                        </Typography> */}
                        <Box sx={{ display: "flex" }}>
                          <Box
                            component={"img"}
                            src={printIcon}
                            alt="Print"
                            onClick={() => handlePrint(row?.referenceNumber)}
                            sx={{
                              cursor: "pointer",
                              mr: { xs: "10px", md: "15px" },
                            }}
                          />
                          <Box
                            component={"img"}
                            src={editIcon}
                            alt="Ediy"
                            onClick={() => handleEdit(row?.referenceNumber)}
                            sx={{ cursor: "pointer" }}
                          />
                        </Box>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            {totalRecords > 0 && (
              <Box
                sx={{ display: "flex", justifyContent: "flex-end", mt: "15px" }}
              >
                <Pagination
                  items={totalRecords}
                  itemsPerPage={20}
                  setPageNumber={setPageNumber}
                  pageNumber={pageNumber}
                />
              </Box>
            )}
          </>
        ) : (
          <Typography
            sx={{ mt: "15px" }}
            className={styles.expense_request_txt}
          >
            There are no pending requests.
          </Typography>
        )}
      </Box>
    );
  };

  const renderExpenseRequestDetails = () => {
    return (
      <>
        <Typography className="sub_heading">Expense Request</Typography>
        {renderFieldsComponent()}
        {formik.values.accountNumber &&
          formik.values.year &&
          formik.values.committee &&
          renderEVoucherRequestsTable()}
      </>
    );
  };

  return (
    <>
      {createExpenseRequest ? (
        <CreateExpenseRequest
          handleBack={() => {
            setCreateExpenseRequest(false);
            setUpdateExpenseRequest(false);
            setReferenceNumber("");
          }}
          referenceNumber={referenceNumber}
          updateExpenseRequest={updateExpenseRequest}
          year={formik?.values?.year}
        />
      ) : (
        <>
          {renderExpenseRequestDetails()}
          {openExpenseRequestDialog && (
            <ViewExpenseRequest
              open={openExpenseRequestDialog}
              handleClose={() => {
                setOpenExpenseRequestDialog(false);
                setViewRequestDetails(false);
              }}
              referenceNumber={referenceNumber}
              viewRequest={viewRequestDetails}
            />
          )}
          {openAddSubAccountDialog && (
            <CreateSubAccount
              open={openAddSubAccountDialog}
              handleClose={handleClose}
              mainAccount={formik.values.committee}
              expenseAccounts={expenseCategories}
              handleAddSubAccount={handleAddSubAccount}
            />
          )}
        </>
      )}
    </>
  );
};

export default ExpenseRequest;
